import { CommonSelector } from "./common-selector";
import { Campaign } from "../../features/reporting/types";
import { Button, Box, ButtonGroup, Skeleton } from "flux";
import { useEffect } from "react";
import { getAccountDisplayName, getAccountSubLabel } from "@adora/shared/utils/account-display";

export interface AllAccountsOption {
  sentinel: string;
  label: string;
}

export interface MultiAccountOption {
  platform: string;
  account_ids: string[];
  account_names?: Record<string, string>;
  campaigns: Campaign[];
  allAccountsOption?: AllAccountsOption;
}

interface CommonRadioGroupProps {
  options: (string | MultiAccountOption)[];
  isLoadingOptions?: boolean;
  selectedValue: string;
  setSelectedValue: (value: string) => void;
  handleOnClick?: (value: string) => void;
  buttonIcons?: React.ComponentType<{ className?: string }>[];
  optionsWithNotifications?: Set<string>;
  size?: "small" | "medium" | "large";
  maxWidth?: string | number;
}

export const LoadingRadioGroup = () => {
  return (
    <Box sx={{ width: "100px", height: "32px" }}>
      <Skeleton variant="rounded" height="100%" />
    </Box>
  );
};

export const CommonRadioGroup = ({
  options,
  isLoadingOptions,
  selectedValue,
  setSelectedValue,
  handleOnClick,
  buttonIcons,
  optionsWithNotifications,
  size = "small",
  maxWidth
}: CommonRadioGroupProps) => {
  // Helper function to determine if an option is selected
  const isOptionSelected = (opt: string | MultiAccountOption) => {
    if (typeof opt === "string") {
      return opt === selectedValue;
    } else {
      return (
        opt.account_ids.includes(selectedValue) ||
        opt.platform === selectedValue ||
        selectedValue === opt.allAccountsOption?.sentinel
      );
    }
  };

  // Helper function to get the notification key for an option
  const getNotificationKey = (opt: string | MultiAccountOption) => {
    if (typeof opt === "string") {
      return opt;
    } else {
      return opt.platform;
    }
  };

  // Initialize selectedValue for object options if not already set
  useEffect(() => {
    if (!selectedValue && options.length > 0) {
      const firstOption = options[0];
      if (typeof firstOption === "string") {
        setSelectedValue(firstOption);
      } else if (firstOption.account_ids.length > 0) {
        setSelectedValue(firstOption.account_ids[0]);
      }
    }
  }, [options, selectedValue, setSelectedValue]);

  // Position-based rounding so the switcher matches the tab group's edges:
  // first entry rounds the left, last entry rounds the right, middle is square.
  const getRoundedClassName = (index: number, total: number) => {
    if (total <= 1) return "rounded-md";
    if (index === 0) return "rounded-l-md rounded-r-none";
    if (index === total - 1) return "rounded-r-md rounded-l-none";
    return "rounded-none";
  };

  // Helper function to render selector for object options
  const renderSelector = (opt: MultiAccountOption, index: number, total: number) => {
    // Build select options with account names using the utility function
    const selectOptions: Array<{ value: string; label: string; subLabel?: string }> = opt.account_ids.map(
      (id, index) => {
        const accountName = opt.account_names?.[id];
        return {
          value: id,
          label: getAccountDisplayName(accountName, index),
          subLabel: getAccountSubLabel(id)
        };
      }
    );

    // Prepend "All Accounts" option if available
    if (opt.allAccountsOption) {
      selectOptions.unshift({
        value: opt.allAccountsOption.sentinel,
        label: opt.allAccountsOption.label
      });
    }

    // For multi-account selectors, currentValue should be an account ID or the all-accounts sentinel
    const currentValue =
      opt.account_ids.includes(selectedValue) || selectedValue === opt.allAccountsOption?.sentinel
        ? selectedValue
        : opt.account_ids[0];
    const selectedOption = selectOptions.find((option) => option.value === currentValue);

    return (
      <CommonSelector
        selectOptions={selectOptions}
        defaultValue={currentValue}
        onChange={(value) => {
          setSelectedValue(value);
          handleOnClick?.(value);
        }}
        customDisplayValue={`${opt.platform}: ${selectedOption?.label || "Account 1"}`}
        width="max-content"
        maxWidth={maxWidth}
        isSelected
        roundedClassName={getRoundedClassName(index, total)}
        heightClassName="h-[31px]"
      />
    );
  };

  return (
    <Box sx={{ maxWidth }}>
      {isLoadingOptions ? (
        <LoadingRadioGroup />
      ) : (
        <ButtonGroup variant="outlined" size="small" aria-label="Basic button group">
          {options.map((opt, index) => {
            const IconComponent = buttonIcons ? buttonIcons[index] : null;
            return (
              <Box key={typeof opt === "string" ? opt : opt.platform} position="relative">
                {typeof opt === "string" || !isOptionSelected(opt) ? (
                  <Button
                    size={size}
                    color="secondary"
                    sx={{
                      backgroundColor: isOptionSelected(opt) ? "rgba(100, 100, 100, 0.15)" : "initial",
                      color: "textSecondary.main",
                      textTransform: "capitalize",
                      width: "max-content",
                      minWidth: "unset",
                      borderColor: "other.outlinedBoder"
                    }}
                    startIcon={IconComponent ? <IconComponent className="h-4 w-4" /> : undefined}
                    onClick={() => {
                      const value = typeof opt === "string" ? opt : opt.platform;
                      setSelectedValue(value);
                      handleOnClick?.(value);
                    }}
                    dataDogIdentifier="common radio group"
                  >
                    {typeof opt === "string" ? opt : opt.platform}
                  </Button>
                ) : (
                  renderSelector(opt, index, options.length)
                )}

                {/* Notification indicator */}
                {optionsWithNotifications?.has(getNotificationKey(opt)) && (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="13"
                    height="12"
                    viewBox="0 0 13 12"
                    fill="none"
                    style={{
                      width: "12px",
                      height: "12px",
                      position: "absolute",
                      right: "0px",
                      top: "0px",
                      zIndex: 2,
                      pointerEvents: "none"
                    }}
                  >
                    <path d="M0.5 0H12.5V12L0.5 0Z" fill="#F96611" />
                  </svg>
                )}
              </Box>
            );
          })}
        </ButtonGroup>
      )}
    </Box>
  );
};
